/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { EditorType } from '@ckeditor/ckeditor5-metadata';

import type { FeatureName } from './capabilities';
import type { ToolbarOptions } from '../types/ToolbarsOptions';
import {
	htmlEditor,
	documentEditing,
	articleEditor,
	microEditor,
	notionLike,
	featureRichEditor,
	balloonEditor,
	emailEditor
} from './editorContents';

type _Preset = {
	name: string;
	id: number;
	description: string;
	features: ReadonlyArray<FeatureName>;
	editorType: EditorType;
	editorContent?: string;
	toolbarOptions: ToolbarOptions;
};

/* eslint-disable max-len */
const _presets = [
	{
		name: 'Collaborative Document Editor',
		id: 0,
		editorContent: documentEditing,
		description:
			'Perfect for reports, academic papers, agreements; with advanced document features and supports all available Collaboration Features.',
		toolbarOptions: {
			isMainToolbarOn: true,
			isBalloonToolbarOn: true,
			isBlockToolbarOn: false,
			isMenuBarUsed: true,
			isCollapsable: true,
			buttonsOptions: 'essential'
		},
		features: [
			'Autoformatting',
			'Automatic Text Transformation',
			'Mentions',
			'Merge Fields',
			'Find and Replace',
			'Paste from Office',
			'Headings',
			'Bold',
			'Italic',
			'Underline',
			'Strikethrough',
			'Subscript',
			'Superscript',
			'Inline Code',
			'Font Styles',
			'Remove Formatting',
			'Indentation',
			'Text Alignment',
			'Links',
			'Horizontal Line',
			'Block Images',
			'Inline Images',
			'Image Insert via URL',
			'Image Resize',
			'Image optimizer by CKBox',
			'Image Upload',
			'Image Styles',
			'Block Image Linking',
			'Image Captions',
			'Image Alt Text',
			'Lists',
			'To-do Lists',
			'List Styles & Properties',
			'Multi-level Lists',
			'Tables',
			'Table Styles & Properties',
			'Table Cell Styles & Properties',
			'Column Resize',
			'Table Captions',
			'Emoji',
			'Special Characters',
			'Slash Commands',
			'Document Outline',
			'Table Of Contents',
			'Templates',
			'Format Painter',
			'Case Change',
			'AI Assistant',
			'Real-time Collaborative Editing (Co-Authoring)',
			'Real-time Collaborative Comments',
			'Real-time Collaborative Track Changes',
			'Real-time Collaborative Revision History',
			'CKBox',
			'Image optimizer by CKBox',
			'Export to Word',
			'Export to PDF',
			'Import from Word',
			'Enhanced Paste from Office',
			'Pagination',
			'Page Breaks',
			'Bookmarks'
		],
		editorType: 'DecoupledEditor'
	},
	{
		name: 'Collaborative Article Editor',
		id: 1,
		editorContent: articleEditor,
		description:
			'General purpose, feature-rich editor perfect for CMS and semantic document creation.',
		toolbarOptions: {
			isMainToolbarOn: true,
			isBalloonToolbarOn: true,
			isBlockToolbarOn: false,
			isMenuBarUsed: true,
			isCollapsable: true,
			buttonsOptions: 'essential'
		},
		features: [
			'Autoformatting',
			'Automatic Text Transformation',
			'Find and Replace',
			'Headings',
			'Bold',
			'Italic',
			'Underline',
			'Strikethrough',
			'Inline Code',
			'Links',
			'Inline Images',
			'Block Images',
			'Image Insert via URL',
			'Image Resize',
			'Image optimizer by CKBox',
			'Image Styles',
			'Block Image Linking',
			'Image Captions',
			'Image Alt Text',
			'Image optimizer by CKBox',
			'Media Embed',
			'Block Quotes',
			'Tables',
			'Table Styles & Properties',
			'Table Cell Styles & Properties',
			'Code Blocks',
			'Horizontal Line',
			'HTML Embed',
			'Emoji',
			'Special Characters',
			'Highlight',
			'Lists',
			'List Styles & Properties',
			'To-do Lists',
			'Indentation',
			'Slash Commands',
			'Document Outline',
			'Table Of Contents',
			'Templates',
			'Case Change',
			'AI Assistant',
			'Real-time Collaborative Editing (Co-Authoring)',
			'Real-time Collaborative Comments',
			'Real-time Collaborative Track Changes',
			'Real-time Collaborative Revision History',
			'CKBox',
			'Export to Word',
			'Export to PDF',
			'Import from Word',
			'Enhanced Paste from Office',
			'Bookmarks'
		],
		editorType: 'ClassicEditor'
	},
	{
		name: 'Feature-rich Editor',
		id: 2,
		editorContent: featureRichEditor,
		description:
			'An editor packed with features to help you easily test most of the functionalities available in CKEditor 5.',
		toolbarOptions: {
			isMainToolbarOn: true,
			isBalloonToolbarOn: false,
			isBlockToolbarOn: false,
			isMenuBarUsed: true,
			isCollapsable: true,
			buttonsOptions: 'essential'
		},
		features: [
			'Autoformatting',
			'Automatic Text Transformation',
			'Mentions',
			'Merge Fields',
			'Find and Replace',
			'Paste from Office',
			'Headings',
			'Bold',
			'Italic',
			'Underline',
			'Strikethrough',
			'Subscript',
			'Superscript',
			'Inline Code',
			'Block Quotes',
			'Font Styles',
			'Remove Formatting',
			'Highlight',
			'Indentation',
			'Text Alignment',
			'Links',
			'Horizontal Line',
			'Block Images',
			'Inline Images',
			'Image Insert via URL',
			'Image Resize',
			'Image optimizer by CKBox',
			'Image Upload',
			'Image Styles',
			'Block Image Linking',
			'Image Captions',
			'Image Alt Text',
			'Lists',
			'To-do Lists',
			'List Styles & Properties',
			'Multi-level Lists',
			'Tables',
			'Table Styles & Properties',
			'Table Cell Styles & Properties',
			'Column Resize',
			'Table Captions',
			'Emoji',
			'Special Characters',
			'Slash Commands',
			'Table Of Contents',
			'Templates',
			'Format Painter',
			'Case Change',
			'CKBox',
			'Image optimizer by CKBox',
			'Export to Word',
			'Export to PDF',
			'Import from Word',
			'Enhanced Paste from Office',
			'Page Breaks',
			'Styles',
			'Bookmarks'
		],
		editorType: 'ClassicEditor'
	},
	{
		name: 'Email Editor',
		id: 14,
		editorContent: emailEditor,
		description:
			'A set of tools for email composition. Provides layout, templates, and styling solutions, as well as cross-platform compatibility.',
		toolbarOptions: {
			isMainToolbarOn: true,
			isBalloonToolbarOn: false,
			isBlockToolbarOn: false,
			isMenuBarUsed: true,
			isCollapsable: true,
			buttonsOptions: 'all'
		},
		features: [
			'AI Assistant',
			'Autoformatting',
			'Automatic Text Transformation',
			'Bold',
			'Column Resize',
			'Email Configuration Helper',
			'Emoji',
			'Enhanced Paste from Office',
			'Enhanced Source Code Editing',
			'Export with Inline Styles',
			'Font Styles',
			'General HTML Support',
			'Headings',
			// Keep inline images above all image plugins. We cannot have block images in the email preset.
			'Inline Images',
			'Image Alt Text',
			'Image Insert via URL',
			'Image Resize',
			'Image Styles',
			'CKBox',
			'Indentation',
			'Italic',
			'Layout Tables',
			'Links',
			'Lists',
			'List Styles & Properties',
			'Merge Fields',
			'Paste from Office',
			'Plain Table Output',
			'Remove Formatting',
			'Strikethrough',
			'Styles',
			'Table Captions',
			'Table Cell Styles & Properties',
			'Table Styles & Properties',
			'Tables',
			'Templates',
			'Text Alignment',
			'Underline'
		],
		editorType: 'ClassicEditor'
	},
	{
		name: 'Notion-like Editor',
		id: 3,
		editorContent: notionLike,
		description:
			'Notion-style editor with block system supports personal and collaborative work across various contexts for content and tasks.',
		toolbarOptions: {
			isMainToolbarOn: true,
			isBalloonToolbarOn: false,
			isBlockToolbarOn: true,
			isMenuBarUsed: false,
			isCollapsable: true,
			buttonsOptions: 'all'
		},
		features: [
			'Autoformatting',
			'Automatic Text Transformation',
			'Find and Replace',
			'Headings',
			'Document Title',
			'Bold',
			'Italic',
			'Underline',
			'Strikethrough',
			'Inline Code',
			'Autoformatting',
			'Highlight',
			'Links',
			'Image Insert via URL',
			'Image Resize',
			'Image optimizer by CKBox',
			'Image Styles',
			'Block Image Linking',
			'Image Captions',
			'Image Alt Text',
			'Media Embed',
			'Block Quotes',
			'Tables',
			'Code Blocks',
			'Horizontal Line',
			'Emoji',
			'Special Characters',
			'Lists',
			'To-do Lists',
			'List Styles & Properties',
			'Tables',
			'Indentation',
			'Slash Commands',
			'Table Of Contents',
			'Templates',
			'Case Change',
			'AI Assistant',
			'Real-time Collaborative Editing (Co-Authoring)',
			'Real-time Collaborative Comments',
			'Real-time Collaborative Track Changes',
			'Real-time Collaborative Revision History',
			'CKBox',
			'Image optimizer by CKBox',
			'Export to Word',
			'Export to PDF',
			'Import from Word',
			'Enhanced Paste from Office'
		],
		editorType: 'BalloonEditor'
	},
	{
		name: 'Microeditor',
		id: 5,
		editorContent: microEditor,
		description:
			'A minimalist inline editor designed for simplicity, offering basic features for quick and easy text editing.',
		toolbarOptions: {
			isMainToolbarOn: true,
			isBalloonToolbarOn: false,
			isBlockToolbarOn: false,
			isMenuBarUsed: false,
			isCollapsable: true,
			buttonsOptions: 'all'
		},
		features: [
			'Autoformatting',
			'Automatic Text Transformation',
			'AI Assistant',
			'Headings',
			'Bold',
			'Italic',
			'Underline',
			'Links',
			'Image Insert via URL',
			'Image Resize',
			'Image optimizer by CKBox',
			'Image Styles',
			'Block Image Linking',
			'Image Captions',
			'Image Alt Text',
			'Tables',
			'Lists',
			'CKBox',
			'Image optimizer by CKBox',
			'Paste from Office',
			'Emoji'
		],
		editorType: 'InlineEditor'
	},
	{
		name: 'Classic Editor',
		id: 6,
		editorContent: articleEditor,
		editorType: 'ClassicEditor',
		description:
			'A Classic editor with some preselected choice of plugins to match some common usage scenarios.',
		toolbarOptions: {
			isMainToolbarOn: true,
			isBalloonToolbarOn: false,
			isBlockToolbarOn: false,
			isMenuBarUsed: false,
			isCollapsable: true,
			buttonsOptions: 'all'
		},
		features: [
			'Autoformatting',
			'Image Insert via URL',
			'Block Quotes',
			'Bold',
			'CKBox',
			'Image optimizer by CKBox',
			'Headings',
			'Image Captions',
			'Block Images',
			'Inline Images',
			'Image Resize',
			'Image Styles',
			'Image Alt Text',
			'Indentation',
			'Italic',
			'Block Image Linking',
			'Lists',
			'List Styles & Properties',
			'Media Embed',
			'Paste from Office',
			'Tables',
			'Table Captions',
			'Table Cell Styles & Properties',
			'Column Resize',
			'Table Styles & Properties',
			'Automatic Text Transformation',
			'To-do Lists',
			'Underline',
			'Emoji'
		]
	},
	{
		name: 'Classic Editor (starter)',
		id: 7,
		editorContent: '',
		editorType: 'ClassicEditor',
		description: 'A starter Classic editor with only a handful of plugins. Start building your personalized editor from scratch!',
		toolbarOptions: {
			isMainToolbarOn: true,
			isBalloonToolbarOn: false,
			isBlockToolbarOn: false,
			isMenuBarUsed: false,
			isCollapsable: true,
			buttonsOptions: 'all'
		},
		features: [ 'Bold', 'Italic', 'Links' ]
	},
	{
		name: 'Document Editor',
		id: 8,
		editorContent: documentEditing,
		description:
			'A Document editor with some preselected choice of plugins to match some common usage scenarios.',
		toolbarOptions: {
			isMainToolbarOn: true,
			isBalloonToolbarOn: true,
			isBlockToolbarOn: false,
			isMenuBarUsed: true,
			isCollapsable: true,
			buttonsOptions: 'essential'
		},
		features: [
			'Autoformatting',
			'Automatic Text Transformation',
			'Mentions',
			'Merge Fields',
			'Find and Replace',
			'Paste from Office',
			'Headings',
			'Bold',
			'Italic',
			'Underline',
			'Strikethrough',
			'Subscript',
			'Superscript',
			'Inline Code',
			'Font Styles',
			'Remove Formatting',
			'Indentation',
			'Text Alignment',
			'Links',
			'Horizontal Line',
			'Block Images',
			'Inline Images',
			'Image Insert via URL',
			'Image Resize',
			'Image optimizer by CKBox',
			'Image Upload',
			'Image Styles',
			'Block Image Linking',
			'Image Captions',
			'Image Alt Text',
			'Lists',
			'To-do Lists',
			'List Styles & Properties',
			'Multi-level Lists',
			'Tables',
			'Table Styles & Properties',
			'Table Cell Styles & Properties',
			'Column Resize',
			'Table Captions',
			'Emoji',
			'Special Characters',
			'Slash Commands',
			'Document Outline',
			'Table Of Contents',
			'Templates',
			'Format Painter',
			'Case Change',
			'CKBox',
			'Image optimizer by CKBox',
			'Export to Word',
			'Export to PDF',
			'Import from Word',
			'Enhanced Paste from Office',
			'Pagination',
			'Page Breaks'
		],
		editorType: 'DecoupledEditor'
	},
	{
		name: 'Document Editor (starter)',
		id: 9,
		editorType: 'DecoupledEditor',
		description: 'A starter Decoupled editor with only a handful of plugins. Start building your personalized editor from scratch!',
		toolbarOptions: {
			isMainToolbarOn: true,
			isBalloonToolbarOn: false,
			isBlockToolbarOn: false,
			isMenuBarUsed: false,
			isCollapsable: true,
			buttonsOptions: 'all'
		},
		features: [ 'Bold', 'Italic', 'Links' ]
	},
	{
		name: 'Inline Editor',
		id: 10,
		editorContent: microEditor,
		editorType: 'InlineEditor',
		description:
			'An Inline editor with some preselected choice of plugins to match some common usage scenarios.',
		toolbarOptions: {
			isMainToolbarOn: true,
			isBalloonToolbarOn: false,
			isBlockToolbarOn: false,
			isMenuBarUsed: false,
			isCollapsable: true,
			buttonsOptions: 'all'
		},
		features: [
			'Autoformatting',
			'Image Insert via URL',
			'Block Quotes',
			'Bold',
			'CKBox',
			'Image optimizer by CKBox',
			'Headings',
			'Image Captions',
			'Block Images',
			'Inline Images',
			'Image Resize',
			'Image Styles',
			'Image Alt Text',
			'Indentation',
			'Italic',
			'Block Image Linking',
			'Lists',
			'List Styles & Properties',
			'Media Embed',
			'Paste from Office',
			'Tables',
			'Table Captions',
			'Table Cell Styles & Properties',
			'Column Resize',
			'Table Styles & Properties',
			'Automatic Text Transformation',
			'To-do Lists',
			'Underline',
			'Emoji'
		]
	},
	{
		name: 'Inline Editor (starter)',
		id: 11,
		editorContent: '',
		editorType: 'InlineEditor',
		description: 'A starter Inline editor with only a handful of plugins. Start building your personalized editor from scratch!',
		toolbarOptions: {
			isMainToolbarOn: true,
			isBalloonToolbarOn: false,
			isBlockToolbarOn: false,
			isMenuBarUsed: false,
			isCollapsable: true,
			buttonsOptions: 'all'
		},
		features: [ 'Bold', 'Italic', 'Links' ]
	},
	{
		name: 'Balloon Editor',
		id: 12,
		editorContent: balloonEditor,
		editorType: 'BalloonEditor',
		description:
			'A Balloon editor with some preselected choice of plugins to match some common usage scenarios.',
		toolbarOptions: {
			isMainToolbarOn: true,
			isBalloonToolbarOn: true,
			isBlockToolbarOn: false,
			isMenuBarUsed: false,
			isCollapsable: true,
			buttonsOptions: 'all'
		},
		features: [
			'Autoformatting',
			'Image Insert via URL',
			'Block Quotes',
			'Bold',
			'CKBox',
			'Image optimizer by CKBox',
			'Headings',
			'Image Captions',
			'Block Images',
			'Inline Images',
			'Image Resize',
			'Image Styles',
			'Image Alt Text',
			'Indentation',
			'Italic',
			'Block Image Linking',
			'Lists',
			'List Styles & Properties',
			'Media Embed',
			'Paste from Office',
			'Tables',
			'Table Captions',
			'Table Cell Styles & Properties',
			'Column Resize',
			'Table Styles & Properties',
			'Automatic Text Transformation',
			'To-do Lists',
			'Underline',
			'Emoji'
		]
	},
	{
		name: 'Balloon Editor (starter)',
		id: 13,
		editorContent: '',
		editorType: 'BalloonEditor',
		description: 'A starter Balloon editor with only a handful of plugins. Start building your personalized editor from scratch!',
		toolbarOptions: {
			isMainToolbarOn: true,
			isBalloonToolbarOn: false,
			isBlockToolbarOn: false,
			isMenuBarUsed: false,
			isCollapsable: true,
			buttonsOptions: 'all'
		},
		features: [ 'Bold', 'Italic', 'Links' ]
	},
	{
		name: 'HTML Editor',
		id: 4,
		editorContent: htmlEditor,
		description:
			'A comprehensive HTML editor equipped with source editing capabilities and a suite of advanced features for web development and design.',
		toolbarOptions: {
			isMainToolbarOn: true,
			isBalloonToolbarOn: false,
			isBlockToolbarOn: false,
			isMenuBarUsed: false,
			isCollapsable: true,
			buttonsOptions: 'all'
		},
		features: [
			'Autoformatting',
			'Automatic Text Transformation',
			'Headings',
			'Bold',
			'Italic',
			'Inline Code',
			'Links',
			'Inline Images',
			'Block Images',
			'Image Insert via URL',
			'Image Resize',
			'Image optimizer by CKBox',
			'Image Styles',
			'Block Image Linking',
			'Image Captions',
			'Image Alt Text',
			'Code Blocks',
			'Tables',
			'Table Captions',
			'Table Styles & Properties',
			'Table Cell Styles & Properties',
			'Column Resize',
			'Lists',
			'List Styles & Properties',
			'General HTML Support',
			'Show Blocks',
			'Enhanced Source Code Editing',
			'HTML Comment Element',
			'HTML Embed',
			'Paste from Office',
			'Enhanced Paste from Office',
			'Emoji'
		],
		editorType: 'ClassicEditor'
	}
] as const satisfies ReadonlyArray<_Preset>;
/* eslint-enable max-len */

export type Preset = Omit<_Preset, 'name'> & {
	name: typeof _presets[ number ][ 'name' ];
};

export const presets = _presets as ReadonlyArray<Preset>;
